import { Box, ButtonBase, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, normalizeLocale, SUPPORTED_LOCALES } from '@/i18n/constants';
import { resolveTokens } from '@/theme/theme';

/**
 * The language row of the account menu (TMMAPP-0100, visual contract §2): a 38px chip per locale
 * (`en` `es` `fr` `pt`), padding 5 0, 12/500, radius `control`; the current one is the `selected`
 * fill with `link` text and border. The choice persists (see i18n.ts `languageChanged`). Each chip
 * is named "{code} · {language}" so the visible code is part of its accessible name.
 */
export function LanguageChips() {
  const { t, i18n } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const current = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LOCALE;

  return (
    <Box role="group" aria-label={t('language.label')} sx={{ display: 'flex', gap: '4px' }}>
      {SUPPORTED_LOCALES.map((code) => {
        const selected = code === current;
        return (
          <ButtonBase
            key={code}
            aria-pressed={selected}
            aria-label={t('language.chip', { code, name: t(`language.${code}`) })}
            lang={code}
            onClick={() => {
              void i18n.changeLanguage(code);
            }}
            sx={{
              width: 38,
              py: '5px',
              fontSize: 12,
              lineHeight: '18px',
              fontWeight: 500,
              borderRadius: `${tokens.radius.control}px`,
              border: '1px solid',
              borderColor: selected ? tokens.link : tokens.line,
              bgcolor: selected ? tokens.selected : 'transparent',
              color: selected ? tokens.link : tokens.text2,
              '&:hover': { bgcolor: selected ? tokens.selected : tokens.hover },
              '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
            }}
          >
            <span aria-hidden>{code}</span>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
